import type { Dispatch, FormEvent, ReactNode, SetStateAction } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded,
  CheckCircleRounded,
  EmailOutlined,
  KeyOutlined,
  LockOutlined,
  PersonOutlineRounded,
  VerifiedUserOutlined,
  VisibilityOffRounded,
  VisibilityRounded,
} from '@mui/icons-material';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import { borderRadius, layoutGap } from '../../../theme/theme';
import RuntimeModeBadge from '../../../shared/ui/feedback/RuntimeModeBadge';
import { OperationProgressIndicator } from '../../../shared/ui/progress';
import { DEVICE_VERIFICATION_ERROR } from '../../../shared/lib/machineFingerprint';

type AuthenticationMode = 'login' | 'register';

interface LoginAuthenticationPanelProps {
  mode: AuthenticationMode;
  username: string;
  password: string;
  email: string;
  activationKey: string;
  showPassword: boolean;
  loading: boolean;
  error: string | null;
  runtimeAvailable: boolean;
  runtimeSubmitBlocked: boolean;
  fingerprintLoading: boolean;
  verifiedFingerprint: string | null;
  fingerprintError: string | null;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
  onToggleMode: () => void;
  setUsername: Dispatch<SetStateAction<string>>;
  setPassword: Dispatch<SetStateAction<string>>;
  setEmail: Dispatch<SetStateAction<string>>;
  setActivationKey: Dispatch<SetStateAction<string>>;
  setShowPassword: Dispatch<SetStateAction<boolean>>;
}

interface CredentialFieldProps {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  autoComplete: string;
  type?: string;
  sx: SxProps<Theme>;
  icon: ReactNode;
  onChange: Dispatch<SetStateAction<string>>;
  endAdornment?: ReactNode;
}

const RequiredMark = () => (
  <Box component="span" aria-hidden="true" sx={{ color: 'primary.light' }}>
    {' '}*
  </Box>
);

function CredentialField({
  id,
  label,
  value,
  placeholder,
  autoComplete,
  type,
  sx,
  icon,
  onChange,
  endAdornment,
}: CredentialFieldProps) {
  const startAdornment = <InputAdornment position="start">{icon}</InputAdornment>;
  const inputSlotProps = endAdornment
    ? { startAdornment, endAdornment }
    : { startAdornment };

  return (
    <Stack spacing={0.45}>
      <Typography component="label" htmlFor={id} sx={fieldLabelSx}>
        {label}<RequiredMark />
      </Typography>
      <TextField
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        fullWidth
        autoComplete={autoComplete}
        required
        sx={sx}
        slotProps={{ input: inputSlotProps }}
      />
    </Stack>
  );
}

function AuthenticationStatus({
  fingerprintLoading,
  runtimeAvailable,
  verifiedFingerprint,
  fingerprintError,
  error,
}: Pick<LoginAuthenticationPanelProps, 'fingerprintLoading' | 'runtimeAvailable' | 'verifiedFingerprint' | 'fingerprintError' | 'error'>) {
  const theme = useTheme();

  return (
    <Stack spacing={0.75}>
      {fingerprintLoading && (
        <Alert
          severity="info"
          icon={<OperationProgressIndicator size={14} ariaLabel="Loading fingerprint" />}
          sx={{ py: 0.3, borderRadius: `${borderRadius.md}px`, alignItems: 'center', fontSize: '0.72rem' }}
        >
          Verifying the connected Revit runtime...
        </Alert>
      )}

      {runtimeAvailable && verifiedFingerprint && (
        <Stack
          role="status"
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{
            minHeight: 42,
            px: 1.15,
            py: 0.65,
            border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            borderRadius: `${borderRadius.md}px`,
            backgroundColor: alpha(theme.palette.success.main, 0.055),
          }}
        >
          <CheckCircleRounded sx={{ color: 'success.main', fontSize: 17, flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.7rem', fontWeight: 750, lineHeight: 1.25 }}>
              Revit runtime verified
            </Typography>
            <Typography
              color="text.secondary"
              sx={{ mt: 0.15, fontFamily: 'monospace', fontSize: '0.61rem', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              Device {verifiedFingerprint.slice(0, 22)}...
            </Typography>
          </Box>
        </Stack>
      )}

      {!fingerprintLoading && !verifiedFingerprint && (fingerprintError || runtimeAvailable) && (
        <Alert severity="warning" sx={{ py: 0.3, borderRadius: `${borderRadius.md}px`, fontSize: '0.72rem' }}>
          {fingerprintError || DEVICE_VERIFICATION_ERROR}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ py: 0.3, borderRadius: `${borderRadius.md}px`, fontSize: '0.72rem' }}>
          {error}
        </Alert>
      )}
    </Stack>
  );
}

function RegistrationFields({
  mode,
  email,
  activationKey,
  setEmail,
  setActivationKey,
  fieldSx,
}: Pick<LoginAuthenticationPanelProps, 'mode' | 'email' | 'activationKey' | 'setEmail' | 'setActivationKey'> & { fieldSx: SxProps<Theme> }) {
  if (mode !== 'register') {
    return null;
  }

  return (
    <>
      <CredentialField
        id="pipeex-email"
        label="Email"
        type="email"
        value={email}
        onChange={setEmail}
        placeholder="name@company.com"
        autoComplete="email"
        sx={fieldSx}
        icon={<EmailOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />}
      />
      <CredentialField
        id="pipeex-activation-key"
        label="Activation key"
        value={activationKey}
        onChange={setActivationKey}
        placeholder="Enter your activation key"
        autoComplete="off"
        sx={fieldSx}
        icon={<KeyOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />}
      />
    </>
  );
}

function SubmitButton({
  mode,
  loading,
  runtimeSubmitBlocked,
}: Pick<LoginAuthenticationPanelProps, 'mode' | 'loading' | 'runtimeSubmitBlocked'>) {
  const theme = useTheme();

  return (
    <Button
      type="submit"
      variant="contained"
      disabled={loading || runtimeSubmitBlocked}
      endIcon={!loading ? <ArrowForwardRounded /> : undefined}
      sx={{
        minHeight: 42,
        mt: layoutGap.dense,
        borderRadius: `${borderRadius.md}px`,
        fontWeight: 750,
        boxShadow: `0 10px 26px ${alpha(theme.palette.primary.main, 0.2)}`,
        '&:hover': { boxShadow: `0 12px 30px ${alpha(theme.palette.primary.main, 0.28)}` },
      }}
    >
      {loading
        ? (mode === 'register' ? 'Creating account...' : 'Signing in...')
        : (mode === 'register' ? 'Register' : 'Sign in')}
    </Button>
  );
}

function ModeToggle({ mode, onToggleMode }: Pick<LoginAuthenticationPanelProps, 'mode' | 'onToggleMode'>) {
  return (
    <Stack direction="row" spacing={layoutGap.dense} alignItems="center" justifyContent="center">
      <Typography color="text.secondary" sx={{ fontSize: '0.7rem' }}>
        {mode === 'register' ? 'Already registered?' : 'New to PipeEX?'}
      </Typography>
      <Button
        type="button"
        variant="text"
        aria-label={mode === 'register' ? 'Already have an account? Sign in' : 'Need an account? Register'}
        onClick={onToggleMode}
        sx={{
          minHeight: 'auto',
          p: 0,
          border: 0,
          fontSize: '0.7rem',
          color: 'primary.light',
          '&:hover': { border: 0, backgroundColor: 'transparent', color: 'primary.main' },
          '&:focus-visible': { border: 0 },
        }}
      >
        {mode === 'register' ? 'Sign in' : 'Create an account'}
      </Button>
    </Stack>
  );
}

function CredentialsForm(props: Pick<LoginAuthenticationPanelProps,
  'mode' | 'username' | 'password' | 'email' | 'activationKey' | 'showPassword' | 'loading'
  | 'runtimeSubmitBlocked' | 'onSubmit' | 'onToggleMode' | 'setUsername'
  | 'setPassword' | 'setEmail' | 'setActivationKey' | 'setShowPassword'> & { fieldSx: SxProps<Theme> }) {
  const {
    mode, username, password, email, activationKey, showPassword, loading, runtimeSubmitBlocked,
    onSubmit, onToggleMode, setUsername, setPassword, setEmail,
    setActivationKey, setShowPassword, fieldSx,
  } = props;
  const passwordToggle = (
    <InputAdornment position="end">
      <IconButton
        aria-label={showPassword ? 'Hide password' : 'Show password'}
        onClick={() => setShowPassword((visible) => !visible)}
        edge="end"
        size="small"
      >
        {showPassword ? <VisibilityOffRounded fontSize="small" /> : <VisibilityRounded fontSize="small" />}
      </IconButton>
    </InputAdornment>
  );

  return (
    <Box component="form" aria-label={mode === 'register' ? 'Register' : 'Sign in'} onSubmit={onSubmit}>
      <Stack spacing={1.25}>
        <CredentialField
          id="pipeex-username"
          label="Username"
          value={username}
          onChange={setUsername}
          placeholder="Enter your username"
          autoComplete="username"
          sx={fieldSx}
          icon={<PersonOutlineRounded sx={{ color: 'text.secondary', fontSize: 18 }} />}
        />
        <CredentialField
          id="pipeex-password"
          label="Password"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={setPassword}
          placeholder="Enter your password"
          autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
          sx={fieldSx}
          icon={<LockOutlined sx={{ color: 'text.secondary', fontSize: 18 }} />}
          endAdornment={passwordToggle}
        />
        <RegistrationFields
          mode={mode}
          email={email}
          activationKey={activationKey}
          setEmail={setEmail}
          setActivationKey={setActivationKey}
          fieldSx={fieldSx}
        />
        <SubmitButton
          mode={mode}
          loading={loading}
          runtimeSubmitBlocked={runtimeSubmitBlocked}
        />
        <ModeToggle mode={mode} onToggleMode={onToggleMode} />
      </Stack>
    </Box>
  );
}

export default function LoginAuthenticationPanel(props: LoginAuthenticationPanelProps) {
  const theme = useTheme();
  const fieldSx: SxProps<Theme> = {
    '& .MuiOutlinedInput-root': {
      minHeight: 42,
      borderRadius: `${borderRadius.md}px`,
      backgroundColor: alpha(theme.palette.background.default, 0.54),
      transition: 'background-color 160ms ease, border-color 160ms ease, box-shadow 160ms ease',
      '& fieldset': { borderColor: alpha(theme.palette.text.primary, 0.22) },
      '&:hover': {
        backgroundColor: alpha(theme.palette.background.default, 0.68),
        '& fieldset': { borderColor: alpha(theme.palette.text.primary, 0.34) },
      },
      '&.Mui-focused': {
        backgroundColor: alpha(theme.palette.background.default, 0.78),
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.13)}`,
      },
    },
    '& .MuiInputBase-input': { py: 1.05, fontSize: '0.78rem' },
  };

  return (
    <Box
      component="section"
      aria-label={props.mode === 'register' ? 'Create a PipeEX account' : 'Sign in to PipeEX'}
      sx={{
        position: 'relative', minWidth: 0, minHeight: 0, overflowY: 'auto', color: 'text.primary',
        backgroundColor: 'background.paper',
        backgroundImage: `linear-gradient(155deg, ${alpha(theme.palette.primary.main, 0.035)} 0%, transparent 42%)`,
        '&::before': {
          content: '""', position: 'absolute', top: 0, left: 0, width: { xs: '100%', md: 3 },
          height: { xs: 2, md: 72 }, backgroundColor: 'primary.main',
        },
      }}
    >
      <Stack spacing={1.6} sx={{
        width: '100%', maxWidth: 390, minHeight: '100%', boxSizing: 'border-box', justifyContent: 'center',
        mx: 'auto', px: { xs: 3, sm: 4 }, py: 3, '@media (max-height: 620px)': { py: 2.25, spacing: 1.3 },
      }}>
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={0.8} alignItems="center" sx={{ minWidth: 0 }}>
            <VerifiedUserOutlined sx={{ color: 'primary.light', fontSize: 17 }} />
            <Typography sx={{ color: 'text.secondary', fontSize: '0.66rem', fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Secure workspace access
            </Typography>
          </Stack>
          <RuntimeModeBadge compact />
        </Stack>
        <Stack direction="row" spacing={1.1} alignItems="center" sx={{ display: { md: 'none' } }}>
          <Box component="img" src={`${process.env.PUBLIC_URL}/logo.png`} alt="" aria-hidden="true" sx={{ width: 38, height: 38, borderRadius: `${borderRadius.circle}` }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', lineHeight: 1.1 }}>PipeEX</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.25, fontSize: '0.64rem' }}>Piping engineering platform</Typography>
          </Box>
        </Stack>
        <Box>
          <Typography component="h1" variant="h4" sx={{ fontWeight: 750, letterSpacing: '-0.025em' }}>
            {props.mode === 'register' ? 'Create account' : 'Sign in'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.55, fontSize: '0.76rem', lineHeight: 1.5 }}>
            {props.mode === 'register' ? 'Create an account for this connected Revit runtime.' : 'Access your PipeEX engineering workspace.'}
          </Typography>
        </Box>
        <AuthenticationStatus {...props} />
        <CredentialsForm {...props} fieldSx={fieldSx} />
      </Stack>
    </Box>
  );
}

const fieldLabelSx: SxProps<Theme> = {
  color: 'text.secondary',
  fontSize: '0.68rem',
  fontWeight: 700,
  letterSpacing: '0.015em',
};
